import { useState } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'
import { api } from '../utils/api'
import RestaurantSearch from './RestaurantSearch'

const MODES = {
  PICK: 'pick',
  EXISTING: 'existing',
  RESTAURANT: 'restaurant',
  RESTAURANT_CONFIRM: 'restaurant-confirm',
}

function ModeCard({ icon, title, description, onClick }) {
  return (
    <button onClick={onClick} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors">
      <div className="flex items-start gap-3">
        <span className="text-2xl">{icon}</span>
        <div>
          <p className="font-medium text-gray-900">{title}</p>
          <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>
    </button>
  )
}

export default function AddMealModal({ date, existingEntry, meals, onClose, onPropose, onMealsUpdated }) {
  const [mode, setMode] = useState(MODES.EXISTING)
  const [search, setSearch] = useState('')
  const [libraryFilter, setLibraryFilter] = useState('all')
  const [dayNotes, setDayNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedMeal, setSelectedMeal] = useState(null)

  const isCounter = existingEntry?.state === 'proposed' || existingEntry?.state === 'countered'

  const filteredMeals = meals.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase())
    const matchesFilter = libraryFilter === 'all' || m.type === libraryFilter
    return matchesSearch && matchesFilter
  })

  async function handleSelectExisting(meal) {
    setSelectedMeal(meal)
  }

  async function handleConfirmExisting() {
    if (!selectedMeal) return
    setLoading(true)
    try {
      await onPropose(date, selectedMeal.mealId, dayNotes || null)
    } finally {
      setLoading(false)
    }
  }

  async function handleSelectRestaurant(place) {
    setLoading(true)
    try {
      const existing = meals.find(m => m.placeId === place.placeId)
      let mealId
      if (existing) {
        mealId = existing.mealId
      } else {
        const result = await api.createMeal({
          type: 'restaurant',
          placeId: place.placeId,
          name: place.name,
          address: place.address,
          mapsUrl: place.mapsUrl,
          phoneNumber: place.phoneNumber,
          website: place.website,
        })
        onMealsUpdated(prev => [...prev, result.meal])
        mealId = result.meal.mealId
      }
      setSelectedMeal({ mealId, ...place })
      setMode(MODES.RESTAURANT_CONFIRM)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleConfirmRestaurant() {
    if (!selectedMeal) return
    setLoading(true)
    try {
      await onPropose(date, selectedMeal.mealId, dayNotes || null)
    } finally {
      setLoading(false)
    }
  }

  const displayDate = new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric'
  })

  function goBack() {
    setMode(MODES.PICK)
    setSelectedMeal(null)
  }

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-4 left-0 right-0 max-w-2xl mx-auto bg-white rounded-2xl z-50 max-h-[92vh] flex flex-col">
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>
        <div className="px-4 pb-3 flex items-center justify-between border-b border-gray-100">
          <div>
            {mode !== MODES.PICK && (
              <button onClick={goBack} className="text-sm text-gray-400 hover:text-gray-600 mb-1">← Back</button>
            )}
            <p className="font-semibold text-gray-900">{isCounter ? 'Counter-propose dinner' : 'Add dinner'}</p>
            <p className="text-xs text-gray-500">{displayDate}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {mode === MODES.PICK && (
            <div className="p-4 flex flex-col gap-3">
              <ModeCard icon="🍳" title="Recipe Library" description="Choose from meals you've already added" onClick={() => setMode(MODES.EXISTING)} />
              <ModeCard icon="🥡" title="Local Carryout / Delivery" description="Find a Richmond-area restaurant via Google Maps" onClick={() => setMode(MODES.RESTAURANT)} />
            </div>
          )}

          {mode === MODES.EXISTING && !selectedMeal && (
            <div className="p-4">
              <input type="search" placeholder="Search meals…" value={search} onChange={e => setSearch(e.target.value)} autoFocus
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 mb-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
              <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'manual', label: '🍳 Home' },
                  { id: 'imported', label: '🌐 Imported' },
                  { id: 'restaurant', label: '🥡 Restaurants' },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setLibraryFilter(tab.id)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${libraryFilter === tab.id ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {tab.label}
                  </button>
                ))}
              </div>
              {filteredMeals.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No meals match</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {filteredMeals.map(meal => (
                    <button key={meal.mealId} onClick={() => handleSelectExisting(meal)}
                      className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors flex items-center gap-3">
                      <span className="text-xl">{meal.type === 'manual' ? '🍳' : meal.type === 'imported' ? '🌐' : '🥡'}</span>
                      <div>
                        <p className="font-medium text-gray-900">{meal.name}</p>
                        <p className="text-xs text-gray-400 capitalize">{meal.type}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {mode === MODES.EXISTING && selectedMeal && (
            <div className="p-4 flex flex-col gap-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{selectedMeal.type === 'manual' ? '🍳' : selectedMeal.type === 'imported' ? '🌐' : '🥡'}</span>
                  <p className="font-semibold text-gray-900">{selectedMeal.name}</p>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium">Notes for this night (optional)</label>
                <textarea value={dayNotes} onChange={e => setDayNotes(e.target.value)}
                  placeholder="e.g. use leftover chicken, grandma is visiting…" rows={2}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
              </div>
            </div>
          )}

          {mode === MODES.RESTAURANT && (
            <RestaurantSearch onSelect={handleSelectRestaurant} loading={loading} />
          )}

          {mode === MODES.RESTAURANT_CONFIRM && selectedMeal && (
            <div className="p-4 flex flex-col gap-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">🥡</span>
                  <p className="font-semibold text-gray-900">{selectedMeal.name}</p>
                </div>
                {selectedMeal.address && <p className="text-sm text-gray-600">📍 {selectedMeal.address}</p>}
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium">What are we ordering? (optional)</label>
                <textarea value={dayNotes} onChange={e => setDayNotes(e.target.value)}
                  placeholder="e.g. tikka masala, garlic naan…" rows={2}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100">
          {mode === MODES.EXISTING && selectedMeal && (
            <button onClick={handleConfirmExisting} disabled={loading} className="btn-primary w-full py-3 text-base">
              {loading ? 'Proposing…' : isCounter ? 'Submit counter-proposal' : 'Propose this dinner'}
            </button>
          )}
          {mode === MODES.RESTAURANT_CONFIRM && (
            <button onClick={handleConfirmRestaurant} disabled={loading} className="btn-primary w-full py-3 text-base">
              {loading ? 'Proposing…' : isCounter ? 'Submit counter-proposal' : 'Propose this dinner'}
            </button>
          )}
        </div>
      </div>
    </>,
  document.body
  )
}
